/**
 * Ayuda — Administración → Ingresos y Gastos
 */

/** @type {import('../help-index.js').HelpSidebarItem} */
const ADM_EXP_CENTERS_ITEM = {
  id: 'centers',
  screenId: 'adm-exp-centers',
  label: 'Centros de ingresos y gastos',
  description: 'Organizá el árbol de conceptos para clasificar ingresos y gastos.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro:
          'Los centros de ingresos y gastos son categorías en forma de árbol. Sirven para clasificar movimientos de dinero: ventas, cobros, sueldos, alquiler, etc. Se usan al registrar pagos, cobros, caja y en reportes de finanzas.',
        paragraphs: [
          'La pantalla se divide en dos bloques: arriba Ingresos y abajo Gastos. Cada uno tiene su propio árbol independiente.',
        ],
      },
      {
        title: 'Leer el árbol',
        bullets: [
          'Código — identificador del concepto (por ejemplo 4000).',
          'Padre — código del nivel superior en el árbol.',
          'Descripción — nombre del concepto.',
          'Imputable — «Imputable» si podés usarlo directamente al cargar un movimiento; «No imputable» si es solo un agrupador.',
          'Flecha ▶ / ▼ — expandir u ocultar subconceptos.',
        ],
        paragraphs: [
          'Los conceptos inactivos se ven atenuados en la lista.',
          'Arriba a la derecha de cada bloque ves cuántos registros hay.',
        ],
      },
      {
        title: 'Expandir y exportar',
        bullets: [
          'Expandir todos / Cerrar todos — abre o cierra todas las ramas del bloque.',
          'Íconos de exportar (arriba de la página) — descargá la lista completa en CSV o Excel.',
        ],
      },
      {
        title: 'Agregar un concepto hijo',
        intro:
          'Debajo de cada rama que tiene hijos (o al final de una rama) aparece «Agregar hijo». Tocá ahí para cargar un subconcepto:',
        fields: [
          { label: 'Código', description: 'Identificador del nuevo concepto. Obligatorio.' },
          { label: 'Descripción', description: 'Nombre que verás en listas y reportes. Obligatorio.' },
          {
            label: 'Imputable',
            description:
              'Marcá si este concepto se puede elegir al imputar un ingreso o gasto. Dejalo sin marcar si solo agrupa otros conceptos.',
          },
        ],
        paragraphs: [
          'Guardar crea el concepto debajo del padre elegido. Cancelar cierra el formulario sin guardar.',
          'No se pueden crear conceptos raíz desde esta pantalla; solo hijos de los que ya existen.',
        ],
      },
      {
        title: 'Editar un concepto',
        intro: 'Tocá el lápiz en la fila. En la ventana podés cambiar:',
        fields: [
          { label: 'Código', description: 'Identificador del concepto.' },
          { label: 'Descripción', description: 'Nombre del concepto.' },
          { label: 'Activo', description: 'Desmarcá para dejar de usar el concepto sin borrar el historial.' },
        ],
        paragraphs: [
          '«Imputable» no se cambia desde la edición; queda como se definió al crearlo.',
          'Guardar confirma los cambios; Cancelar sale sin modificar.',
        ],
      },
      {
        title: 'Ingresos vs Gastos',
        bullets: [
          'Ingresos — entradas de dinero (ventas, otros ingresos). Bloque con icono verde hacia arriba.',
          'Gastos — salidas de dinero (compras, sueldos, servicios). Bloque con icono rojo hacia abajo.',
        ],
        paragraphs: [
          'Las operaciones (agregar hijo, editar, expandir) funcionan igual en ambos bloques.',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpScreen} */
export const ADM_EXP_HELP = {
  screenId: 'adm-exp',
  label: 'Ingresos y Gastos',
  navPath: 'Administración → Ingresos y Gastos',
  keywords: [
    'ingreso', 'ingresos', 'gasto', 'gastos', 'centro de costos', 'centro de ingresos',
    'imputable', 'árbol', 'clasificación', 'egreso',
  ].map((k) => k.toLowerCase()),
  status: 'draft',
  summary:
    'Acá configurás el árbol de conceptos para clasificar ingresos y gastos del negocio. Hay dos bloques: Ingresos y Gastos. Tocá abajo para ver cómo agregar, editar y exportar.',
  sidebarItems: [ADM_EXP_CENTERS_ITEM],
}
